// src/features/landing/helpers.ts
// Pure helpers for the landing module — scope/entity construction, list query
// params, section defaults, formatting. No React imports.

import type { Entity } from '@core/types/entity';
import type { LandingPageStatus, LandingSection, LandingSectionType } from '@core/types/landing';

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

/** Extract an error message suitable for display. */
export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function listParams(pagination: { cursor?: string; limit?: number }, filters: Record<string, any> = {}): any {
  return {
    pagination: { cursor: pagination.cursor ?? '', limit: pagination.limit ?? 50, direction: 'forward' },
    ...filters,
  };
}

export function formatDate(iso?: string, locale?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export function genId(): string {
  return `sec_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const LANDING_STATUSES: LandingPageStatus[] = [
  'LANDING_PAGE_STATUS_DRAFT',
  'LANDING_PAGE_STATUS_PUBLISHED',
  'LANDING_PAGE_STATUS_ARCHIVED',
  'LANDING_PAGE_STATUS_SCHEDULED',
];

export function statusBadgeVariant(status?: LandingPageStatus): 'default' | 'success' | 'warning' | 'secondary' | 'primary' {
  switch (status) {
    case 'LANDING_PAGE_STATUS_PUBLISHED':
      return 'success';
    case 'LANDING_PAGE_STATUS_SCHEDULED':
      return 'warning';
    case 'LANDING_PAGE_STATUS_ARCHIVED':
      return 'secondary';
    default:
      return 'primary';
  }
}

export const SECTION_TYPES: LandingSectionType[] = [
  'LANDING_SECTION_TYPE_HERO',
  'LANDING_SECTION_TYPE_TEXT',
  'LANDING_SECTION_TYPE_IMAGE',
  'LANDING_SECTION_TYPE_GALLERY',
  'LANDING_SECTION_TYPE_CTA',
  'LANDING_SECTION_TYPE_FEATURES',
  'LANDING_SECTION_TYPE_FORM',
  'LANDING_SECTION_TYPE_BOOKING',
  'LANDING_SECTION_TYPE_VIDEO',
  'LANDING_SECTION_TYPE_FAQ',
  'LANDING_SECTION_TYPE_TESTIMONIALS',
  'LANDING_SECTION_TYPE_CONTACTS',
  'LANDING_SECTION_TYPE_MAP',
  'LANDING_SECTION_TYPE_HTML',
  'LANDING_SECTION_TYPE_DIVIDER',
];

/** A fresh, empty section of the given type, ready to edit. */
export function newSection(type: LandingSectionType, order: number): LandingSection {
  const base: LandingSection = { id: genId(), section_type: type, order, hidden: false };
  switch (type) {
    case 'LANDING_SECTION_TYPE_HERO':
      return { ...base, hero: { heading: '', layout: 'HERO_LAYOUT_CENTER' } };
    case 'LANDING_SECTION_TYPE_TEXT':
      return { ...base, text: { body: '', alignment: 'TEXT_ALIGNMENT_LEFT' } };
    case 'LANDING_SECTION_TYPE_IMAGE':
      return { ...base, image: { image_url: '' } };
    case 'LANDING_SECTION_TYPE_GALLERY':
      return { ...base, gallery: { items: [], layout: 'GALLERY_LAYOUT_GRID' } };
    case 'LANDING_SECTION_TYPE_CTA':
      return { ...base, cta: { primary_button: { label: '', url: '', open_new_tab: false } } };
    case 'LANDING_SECTION_TYPE_FEATURES':
      return { ...base, features: { items: [], columns: 3 } };
    case 'LANDING_SECTION_TYPE_FORM':
      return { ...base, form: { form: undefined } };
    case 'LANDING_SECTION_TYPE_BOOKING':
      return { ...base, booking: { bookable_resource: undefined, widget_mode: 'BOOKING_WIDGET_MODE_CALENDAR' } };
    case 'LANDING_SECTION_TYPE_VIDEO':
      return { ...base, video: { video_url: '', autoplay: false } };
    case 'LANDING_SECTION_TYPE_FAQ':
      return { ...base, faq: { items: [] } };
    case 'LANDING_SECTION_TYPE_TESTIMONIALS':
      return { ...base, testimonials: { items: [], layout: 'TESTIMONIALS_LAYOUT_GRID' } };
    case 'LANDING_SECTION_TYPE_CONTACTS':
      return { ...base, contacts: { social_links: [] } };
    case 'LANDING_SECTION_TYPE_MAP':
      return { ...base, map: { latitude: 0, longitude: 0, zoom: 15 } };
    case 'LANDING_SECTION_TYPE_HTML':
      return { ...base, html: { html: '' } };
    default:
      return base;
  }
}

export function sectionLabel(type?: LandingSectionType): string {
  return (type ?? '').replace('LANDING_SECTION_TYPE_', '');
}

export function parseClearableInt(raw: string): number | undefined {
  const digits = raw.replace(/[^0-9]/g, '');
  return digits === '' ? undefined : parseInt(digits, 10);
}

export function parseClearableFloat(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isNaN(n) ? undefined : n;
}
